/**
 * Read View Schemas
 * 
 * Authority: READ_VIEW_LAW.md + RV_NAV_CAPABILITY_LAW.md
 * Enforcement: Gate 15 (UI Detail View Capability)
 * 
 * Centralized schema definitions for all entities.
 * No entity-specific logic allowed in StandardReadView.
 * 
 * Schema Structure:
 * - required: Fields that must exist
 * - optional: Fields that may exist
 * - fallbacks: Default values for missing fields
 * - sections: Structured sections for detail view rendering
 */

export const kbSchema = {
    required: ['id', 'display_name'],
    optional: ['content_summary', 'content_raw', 'tags', 'created_at', 'updated_at', 'source_url', 'source_type'],
    fallbacks: {
        display_name: 'Untitled Entry',
        content_summary: 'No summary available',
        content_raw: 'No content available',
        tags: []
    },
    // ... sections ...
};

export const toolSchema = {
    required: ['id', 'display_name'],
    optional: ['description', 'use_case', 'alternatives', 'friction_notes', 'tags', 'category', 'status'],
    fallbacks: {
        display_name: 'Untitled Tool',
        description: 'No description available', // noscan — schema fallback value
        tags: []
    },
    // ... sections ...
};

export const startupSchema = {
    required: ['id', 'display_name'],
    optional: ['description', 'body', 'metadata', 'tags', 'created_at', 'updated_at', 'sector', 'stage', 'website'],
    fallbacks: {
        display_name: 'Untitled Startup',
        description: 'No description available', // noscan — schema fallback value
        tags: []
    },
    // ...
};

export const investorSchema = {
    required: ['id', 'display_name'],
    optional: ['description', 'body', 'metadata', 'tags', 'created_at', 'updated_at', 'type', 'focus', 'website'],
    fallbacks: {
        display_name: 'Untitled Investor',
        description: 'No description available', // noscan — schema fallback value
        tags: []
    },
    // ...
};

export const workInboxSchema = {
    required: ['id', 'display_name'],
    // ...
    fallbacks: {
        display_name: 'Untitled Item',
        description: 'No details available' // noscan — schema fallback value
    },
    sections: [
        {
            id: 'summary',
            label: 'Summary',
            field: 'description', // Map description to summary section
            fallback: ['summary', 'content_summary'],
            renderType: 'text'
        },
        {
            id: 'payload',
            label: 'Payload Data', // noscan — schema section label (technical term in config context)
            field: 'payload',
            fallback: [],
            renderType: 'json'
        },
        {
            id: 'metadata',
            label: 'Metadata',
            field: 'meta',
            fallback: [],
            renderType: 'object'
        }
    ]
};

export const founderSchema = {
    required: ['id', 'display_name'],
    optional: ['email', 'startup_name', 'contact_status', 'email_status', 'linked_in', 'location', 'bio', 'tags'],
    fallbacks: {
        display_name: 'Unknown Founder',
        email: 'No email provided',
        tags: []
    },
    // ...
    sections: [
        {
            id: 'bio',
            label: 'Bio',
            field: 'bio',
            fallback: ['description', 'summary'],
            renderType: 'text'
        },
        {
            id: 'startup',
            label: 'Startup',
            field: 'startup_name',
            fallback: [],
            renderType: 'text'
        },
        {
            id: 'contact',
            label: 'Contact Status', // noscan — schema section label
            field: 'contact_status',
            fallback: [],
            renderType: 'text'
        },
        {
            id: 'email_status',
            label: 'Email Status', // noscan — schema section label
            field: 'email_status',
            fallback: [],
            renderType: 'text'
        },
        {
            id: 'tags',
            label: 'Tags',
            field: 'tags',
            fallback: [],
            renderType: 'list'
        }
    ]
};

// Schema Adapter Helpers moved to src/adapters/ for single-authority enforcement.
